import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';

const MemberCard = ({ member, isKeyMember = false }) => {
  const {
    twitch_username,
    display_name,
    role,
    bio,
    website,
    profile_image_url,
    is_live,
    account_status
  } = member;

  // Determine if this is an enhanced member card (key members with bios/websites)
  const hasEnhancedInfo = bio || website;

  return (
    <div className={`card ${isKeyMember && hasEnhancedInfo ? 'key-member-card' : ''}`} 
         style={{marginBottom: '1rem'}}>
      <div style={{display: 'flex', alignItems: 'flex-start', gap: '1rem'}}>
        {/* Profile Image */}
        <div style={{position: 'relative', flexShrink: '0'}}>
          <img
            src={profile_image_url || `https://via.placeholder.com/80x80/4a9d4a/ffffff?text=${display_name?.charAt(0) || 'M'}`}
            alt={display_name}
            style={{
              width: isKeyMember && hasEnhancedInfo ? '100px' : '80px',
              height: isKeyMember && hasEnhancedInfo ? '100px' : '80px',
              borderRadius: '50%',
              objectFit: 'cover'
            }}
          />
          {/* Account status badge */}
          {account_status && (
            <div style={{
              position: 'absolute',
              bottom: '-8px',
              left: '50%',
              transform: 'translateX(-50%)',
              fontSize: '0.625rem',
              padding: '2px 6px',
              borderRadius: '10px',
              backgroundColor: account_status === 'partner' ? '#9146ff' : '#00c851',
              color: 'white',
              fontWeight: 'bold',
              textTransform: 'uppercase'
            }}>
              {account_status}
            </div>
          )}
        </div>

        {/* Member Info */}
        <div style={{flex: '1', minWidth: '0'}}>
          <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem', flexWrap: 'wrap'}}>
            <h3 style={{
              fontSize: isKeyMember && hasEnhancedInfo ? '1.375rem' : '1.125rem',
              fontWeight: 'bold',
              color: 'var(--forest-800)',
              margin: '0'
            }}>
              {display_name || twitch_username}
            </h3>
            <span style={{
              fontSize: '0.75rem',
              backgroundColor: 'var(--forest-100)',
              color: 'var(--forest-700)',
              padding: '0.25rem 0.5rem',
              borderRadius: '9999px'
            }}>
              {role}
            </span>
          </div>

          {/* Bio - only show for key members */}
          {bio && (
            <p style={{
              color: 'var(--forest-700)',
              fontSize: '0.875rem',
              marginBottom: '0.75rem',
              lineHeight: '1.5',
              fontStyle: 'italic',
              backgroundColor: 'var(--forest-50)',
              padding: '0.75rem',
              borderRadius: '0.5rem',
              borderLeft: '4px solid var(--forest-300)'
            }}>
              {bio}
            </p>
          )}

          {/* Action Links */}
          <div style={{display: 'flex', gap: '0.5rem', flexWrap: 'wrap'}}>
            {/* Live indicator - show as first item when live */}
            {is_live && (
              <span style={{
                fontSize: '0.75rem',
                backgroundColor: '#ef4444',
                color: 'white',
                padding: '0.25rem 0.75rem',
                borderRadius: '9999px',
                fontWeight: 'bold',
                animation: 'pulse 2s infinite',
                boxShadow: '0 0 10px rgba(239, 68, 68, 0.5)',
                textTransform: 'uppercase'
              }}>
                🔴 LIVE
              </span>
            )}
            <a
              href={`https://twitch.tv/${twitch_username}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: '0.75rem',
                backgroundColor: '#9146ff',
                color: 'white',
                padding: '0.25rem 0.75rem',
                borderRadius: '9999px',
                textDecoration: 'none',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#7c3aed'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#9146ff'}
            >
              Twitch
            </a>
            {website && (
              <a
                href={website}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontSize: '0.75rem',
                  backgroundColor: 'var(--earth-400)',
                  color: 'white',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '9999px',
                  textDecoration: 'none',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = 'var(--earth-500)'}
                onMouseOut={(e) => e.target.style.backgroundColor = 'var(--earth-400)'}
              >
                Website
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const TeamSection = ({ title, members, liveMembers = [], isKeySection = false }) => {
  const [isExpanded, setIsExpanded] = useState(isKeySection || title === 'Founder' || title === 'Members');

  if (!members || members.length === 0) return null;

  // Determine grid layout: Members section uses 2-wide, others use 1-wide
  const getGridClass = () => {
    if (title === 'Members') {
      return members.length > 1 ? 'grid-2' : 'grid-1';
    }
    return 'grid-1';
  };

  return (
    <div style={{marginBottom: '2rem'}}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          width: '100%',
          padding: '1rem',
          backgroundColor: isKeySection ? 'var(--forest-100)' : 'var(--forest-50)',
          border: 'none',
          borderRadius: '0.5rem',
          cursor: 'pointer',
          marginBottom: '1rem',
          transition: 'all 0.3s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
        onMouseOver={(e) => {
          e.target.style.backgroundColor = isKeySection ? 'var(--forest-200)' : 'var(--forest-100)';
        }}
        onMouseOut={(e) => {
          e.target.style.backgroundColor = isKeySection ? 'var(--forest-100)' : 'var(--forest-50)';
        }}
      >
        <h2 style={{
          fontSize: '1.5rem',
          fontWeight: 'bold',
          color: 'var(--forest-800)',
          margin: '0'
        }}>
          {title}
        </h2>
        <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
          <span style={{fontSize: '0.875rem', color: 'var(--forest-600)'}}>
            {members.length} member{members.length !== 1 ? 's' : ''}
          </span>
          <span style={{
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s ease',
            fontSize: '1.25rem'
          }}>
            ▼
          </span>
        </div>
      </button>

      {isExpanded && (
        <div className={`grid ${getGridClass()}`}>
          {members.map((member, index) => (
            <MemberCard
              key={`${member.twitch_username}-${index}`}
              member={member}
              isKeyMember={isKeySection}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const Team = () => {
  const [teamMembers, setTeamMembers] = useState({
    founder: [],
    directors: [],
    advisors: [],
    members: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [membershipStatus, setMembershipStatus] = useState('enabled'); // 'enabled' or 'disabled'

  useEffect(() => {
    fetchTeamData();
  }, []);

  const fetchTeamData = async () => {
    try {
      const response = await apiService.getTeamMembers();
      setTeamMembers(response.data);
      
      // Get membership status from API or use default
      try {
        const statsResponse = await apiService.getStatisticsData();
        if (statsResponse.data && statsResponse.data.team && statsResponse.data.team.membershipStatus) {
          setMembershipStatus(statsResponse.data.team.membershipStatus);
        }
      } catch (statsError) {
        console.warn('Could not fetch membership status:', statsError);
      }
      
      setError(null);
    } catch (error) {
      console.error('Error fetching team data:', error);
      setError('Failed to load team members. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Count live members across all categories
  const liveCount = Object.values(teamMembers).flat().filter(member => member.is_live).length;
  const totalCount = Object.values(teamMembers).flat().length;

  if (loading) {
    return (
      <div style={{minHeight: '100vh', padding: '4rem 1rem'}}>
        <div className="container text-center">
          <div className="spinner"></div>
          <p style={{color: 'var(--forest-600)', marginTop: '1rem'}}>Loading team members...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{minHeight: '100vh', padding: '4rem 1rem'}}>
        <div className="container text-center">
          <p style={{color: 'var(--earth-600)', marginBottom: '1rem'}}>⚠️ {error}</p>
          <button onClick={fetchTeamData} className="btn btn-outline">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{minHeight: '100vh', padding: '4rem 1rem'}}>
      <div className="container">
        {/* Page Header */}
        <div className="text-center mb-16">
          <h1 className="text-gradient" style={{
            fontSize: '3rem',
            fontWeight: 'bold',
            marginBottom: '1.5rem'
          }}>
            Meet The Canopy Team
          </h1>
          <p style={{
            fontSize: '1.125rem',
            color: 'var(--forest-600)',
            maxWidth: '48rem',
            margin: '0 auto'
          }}>
            Our diverse team of content creators, each bringing their unique personality, 
            skills, and passion to the streaming community. Together, we create an 
            inclusive environment where creativity flourishes.
          </p>
        </div>

        {/* Team Stats */}
        {totalCount > 0 && (
          <div className="grid grid-2 text-center mb-12">
            <div className="card" style={{padding: '1rem'}}>
              <div style={{fontSize: '2rem', fontWeight: 'bold', color: 'var(--forest-600)', marginBottom: '0.5rem'}}>
                {totalCount}
              </div>
              <div style={{color: 'var(--forest-800)', fontWeight: '500'}}>Total Members</div>
            </div>
            <div className="card" style={{padding: '1rem'}}>
              <div style={{fontSize: '2rem', fontWeight: 'bold', color: 'var(--red-600)', marginBottom: '0.5rem'}}>
                {liveCount}
              </div>
              <div style={{color: 'var(--forest-800)', fontWeight: '500'}}>Currently Live</div>
            </div>
          </div>
        )}

        {/* Live Members Alert */}
        {liveCount > 0 && (
          <div style={{
            backgroundColor: '#fef2f2',
            borderLeft: '4px solid #ef4444',
            padding: '1rem',
            marginBottom: '2rem',
            borderRadius: '0.5rem'
          }}>
            <div style={{display: 'flex', alignItems: 'flex-start'}}>
              <span style={{fontSize: '1.25rem', marginRight: '0.75rem'}}>🔴</span>
              <div>
                <p style={{fontSize: '0.875rem', color: '#dc2626', margin: '0'}}>
                  <strong>{liveCount} team member{liveCount !== 1 ? 's' : ''} currently live!</strong>{' '}
                  Check out their streams on our home page or click their Twitch links below.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Team Sections */}
        <div className="space-y-6">
          <TeamSection 
            title="Founder" 
            members={teamMembers.founder || []} 
            isKeySection={true}
          />
          <TeamSection 
            title="Directors" 
            members={teamMembers.directors || []} 
            isKeySection={true}
          />
          <TeamSection 
            title="Advisors" 
            members={teamMembers.advisors || []} 
            isKeySection={true}
          />
          <TeamSection 
            title="Members" 
            members={teamMembers.members || []} 
            isKeySection={true}
          />
        </div>

      </div>
    </div>
  );
};

export default Team;